import { Injectable } from '@angular/core';
import { Observable, Subject, Subscription, first } from 'rxjs';
import { webSocket, WebSocketSubject } from 'rxjs/webSocket';
import { ScrEnvironment } from 'src/environments/environment';
import { ScrAuthenticationService, SignInStateEvent } from './authentication.service';
import { IScrBaseResponse, ScrBaseResponse, ScrBaseService, ScrMessage } from './base.service';
import { ScrServiceManager } from './servicemanager.service';
import { ScrBusyInterceptor } from './busy.interceptor';
import { CacheService } from './cache.service';
import { BadgeService } from './badge.service';
import { Config, ConfigService } from './config.service';

class OutstandingMessage<T> {

  public timeout?: number;

  constructor(public s: Subject<T> | undefined, public spinner: boolean, public correlationId: string, public timestamp = new Date()) {

  }
}

export class WebsocketMessage {

  public correlationId: string;
  public token?: string;

  constructor(public action: string, public operation: string, data?: object) {

    this.correlationId = `${new Date().valueOf()}`;

    if (data) {
      Object.assign(this, data);
    }
  }
}

export class WsBaseResponse extends ScrBaseResponse {
  public correlationId?: string;

}

export interface IWsBaseResponse extends IScrBaseResponse {
  correlationId?: string;
}

export class ClientInputWebsocketMessage extends WebsocketMessage {
  constructor(public msg: string, public responseDelay: string, public payload?: string) {
    super('clientmessage', 'send');
  }
}

export interface IWsBaseRequest {
  action: string;
  status?: string;
}

export interface CacheInvalidationRequest extends IWsBaseRequest {
  key: string;
  message?: string;
  title?: string;
  messageType?: string;
}

export interface AlertRequest extends IWsBaseRequest {
  message: string;
  title: string;
}



@Injectable({
  providedIn: 'root'
})
export class WebSocketService extends ScrBaseService {

  public ws?: WebSocketSubject<object>;
  public signedIn? = false;
  private interval?: number;
  private subscription?: Subscription;
  private debug = false;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private outstandingMessages = new Map<string, OutstandingMessage<any>>();
  private customerSupportPhone = '';

  constructor(
    private configService: ConfigService,
    private serviceManager: ScrServiceManager,
    private environment: ScrEnvironment,
    private cacheService: CacheService,
    private badgeService: BadgeService,
    private authenticationService: ScrAuthenticationService,
    private interceptor?: ScrBusyInterceptor) {
    super(serviceManager);

    configService.getConfig().subscribe((cfg: Config) => {
      this.customerSupportPhone = cfg.customerSupportPhone;
    });

    if (this.authenticationService.isUserSignedIn()) {
      this.signedIn = true;
      if (this.debug) console.debug('user is signed in.  get token then subscribe');
      this.authenticationService.getIdToken().then(t => {
        if (this.debug) console.debug('user is signed in.  got token');
        if (this.ws) {
          if (this.debug) console.debug('ws exists (1).  unsubscribe');
          this.ws.unsubscribe();
        }
        if (this.debug) console.debug('ws created (1).');
        this.ws = webSocket<object>(this.environment.ws);
        this.subscribe(t);
      });
    }

    this.authenticationService.signInState.subscribe((e: SignInStateEvent) => {
      this.signedIn = e.signedIn;
      if (e.signedIn) {
        if (this.debug) console.debug('user has signed in or auth state updated.  get token then subscribe/auth');
        this.authenticationService.getIdToken().then(t => {
          if (this.debug) console.debug('user has signed in.  got token');
          if (this.ws) {
            if (this.debug) console.debug('ws exists (2).  reauthorize');
            this.authenticate(t);
          }
          if (this.debug) console.debug('ws created (2).');
          this.ws = webSocket<object>(this.environment.ws);
          this.subscribe(t);
        });
      }
      else {
        if (this.debug) console.debug('user signed out.');
        if (this.ws) {
          if (this.debug) console.debug('ws.unsubscribe');
          this.ws.unsubscribe();
          this.ws = undefined;
        }
        // sign out of web socket.
      }
    });
  }

  public async bounce(): Promise<void> {
    this.ws?.complete();
  }

  public isOpen(): boolean {
    return this.ws !== undefined;
  }

  /**
   * Send a message on the web socket with <T> being the response type expected.
   */
  public send<T extends WsBaseResponse>(msg: WebsocketMessage, spinner = true, maxDuration = 120_000, authenticated = true): Observable<T> {


    const s = new Subject<T>();


    if (!this.ws) {
      setTimeout(() => { s.error('websocket not open'); }, 500);
      return s;
    }

    const outMsg = new OutstandingMessage<T>(s, spinner, msg.correlationId);

    if (msg.correlationId) {
      this.outstandingMessages.set(msg.correlationId, outMsg);
    }

    console.log('sending ' + JSON.stringify(msg));

    this.authenticationService.getIdToken().then(t => {
      if (authenticated) {
        if (this.debug) console.debug('send, got id token.  ' + t);
        msg.token = t;
      }
      if (!this.ws) {
        setTimeout(() => { s.error('websocket not open'); }, 500);
        return;
      }
      this.ws.next(msg);

      if (this.debug) console.debug('set message timeout to ' + maxDuration + ' milliseconds.');
      outMsg.timeout = window.setTimeout((msg: OutstandingMessage<T>) => {
        if (this.debug) console.debug('websocket response timeout after 2 min!');
        const txt = 'The process has timed out as a result of interrupted communications. Please check your email to determine whether your request was completed. If you have any questions or concerns, please contact Customer Support at ' + this.customerSupportPhone + '.';
        const m = new WsBaseResponse('failed', [new ScrMessage('5101', txt)]) as T;
        if (msg.s) {
          msg.s.next(m);
          msg.s.complete();
        }
        this.outstandingMessages.delete(msg.correlationId);
        if (msg.spinner) {
          // dismiss spinner on 1st response.
          // might needs this...
          this.interceptor?.decrement();
          msg.spinner = false;
        }
      }, maxDuration, outMsg);

    }).catch(e => {
      console.error('caught error fetching id token for ws request');
      console.error(e);
      s.error(e);
    });

    if (spinner) {
      if (this.debug) console.debug('increment spinner');
      this.interceptor?.increment();
    }

    return s;
  }

  private subscribe(token?: string) {
    if (!this.ws) {
      return;
    }

    if (this.debug) console.debug('subscribe.');


    if (this.subscription) {
      if (this.debug) console.debug('first unsubscribe.');
      this.subscription.unsubscribe();
    }

    if (this.debug) console.debug('calling ws subscribe');
    this.subscription = this.ws.subscribe({
      next: (message) => {
        try {
          if (this.debug) console.debug('got ' + JSON.stringify(message));
          // message could be an incoming request or a response to a previously client-sent message.

          if (Object.prototype.hasOwnProperty.call(message, 'action')) {
            // message has 'action' so it is an incoming request (not a response).
            const m = message as IWsBaseRequest;
            if (m.action === 'ping') {
              this.ws?.next({ 'action': 'pong' });
            }
            else if (m.action === 'pong') {
              // server sent me a pong keep alive.  Just consume it.
            }
            else if (m.action === 'cacheinvalidate') {
              const cacheMsg = message as CacheInvalidationRequest;
              if (cacheMsg.key && cacheMsg.key === "badgeCounts") {
                this.badgeService.getBadges(true).subscribe(() => {
                  // side effect is to update badges on app.  Nothing we need to do here.
                });
              }
              else if (cacheMsg.key === 'config') {
                setTimeout(() => {
                  // wait a random number of seconds between 0 and 10.
                  this.configService.doGet().pipe(first()).subscribe(() => {
                    console.debug("config reloaded");
                  });
                }, Math.floor(Math.random() * 10000));
              }
              else if (cacheMsg.key) {
                this.cacheService.invalidate(cacheMsg.key, false);
              }

              if (cacheMsg.message && cacheMsg.title) {
                this.serviceManager.emitChange({
                  body: cacheMsg.message,
                  title: cacheMsg.title,
                  type: cacheMsg.messageType ? cacheMsg.messageType : 'info'
                });
              }
            }
            else if (m.action === 'alert') {
              const alertMsg = message as AlertRequest;
              this.serviceManager.emitChange({
                body: alertMsg.message,
                title: alertMsg.title
              });
            }
            else if (m.action === 'servermessage') {
              // response to 'clientmessage'
              const serverMessage = message as IWsBaseResponse;
              this.processCorrelatedMessage(serverMessage);
            }
          }
          else {
            const m = message as WsBaseResponse;
            if (m.correlationId) {
              this.processCorrelatedMessage(m);
            }
            else {
              console.info('unexpected message ' + JSON.stringify(message));
            }
          }
        }
        catch (error) {
          console.error(error);
        }
      },
      error: (e) => {
        if (e.type === 'close') {
          console.log('web socket was closed at ' + new Date());
          this.ws = undefined;
          if (this.signedIn) {
            this.tryReconnect();
          }
        }
        else if (e.type === 'error') {
          console.log('web socket connection error at ' + new Date());
          this.ws = undefined;
          if (this.signedIn) {
            this.tryReconnect();
          }
        }
        else {
          console.info(e);
        }
      },
      complete: () => {
        if (this.signedIn) {
          this.ws = undefined;
          console.log('ws complete.  user is signed in.  try reconnecting.  ' + new Date());
          this.tryReconnect();
        }
        else {
          console.log('ws complete.  not signed in');
        }
      }
    });

    this.authenticate(token);

    if (this.interval) {
      window.clearInterval(this.interval);
    }
    this.interval = window.setInterval(() => {
      if (this.ws) {
        this.ws.next({ 'action': 'ping' });
      }
    }, 120_000); // 2 min * 60 sec * 1000 milliseconds
  }

  private processCorrelatedMessage(m: WsBaseResponse): void {
    if (m.correlationId) {
      const sc = this.outstandingMessages.get(m.correlationId);
      if (sc) {
        if (sc.timeout) {
          window.clearTimeout(sc.timeout);
          delete sc.timeout;
        }
        if (sc.s) {
          sc.s.next(m);
          sc.s.complete();
        }
        this.outstandingMessages.delete(m.correlationId);
        if (sc.spinner) {
          // dismiss spinner on 1st response.
          this.interceptor?.decrement();
          sc.spinner = false;
        }
      }
      else {
        console.info('no caller subject context entry for ' + m.correlationId);
      }
    }
  }


  private authenticate(token?: string) {
    if (token) {
      const authMsg = {
        correlationId: `${new Date().valueOf()}`,
        action: 'authenticate',
        token
      };
      if (this.ws) {
        const outMsg = new OutstandingMessage(undefined, false, authMsg.correlationId);
        this.outstandingMessages.set(authMsg.correlationId, outMsg);
        this.ws.next(authMsg);
      }
      else {
        console.log('cannot authenticate as ws is not defined');
      }
    }
  }

  private tryReconnect(): void {
    setTimeout(() => {
      if (this.debug) console.debug('tryReconnect timeout.  Attempt to connect if signed in.');
      // only try reconnecting when signed in.
      // if sign in state changes in the future,
      // we will try to connect then.
      if (this.signedIn) {
        if (this.debug) console.debug('tryReconnect: user is signed in.  get token then subscribe');
        this.authenticationService.getIdToken().then(t => {
          if (this.debug) console.debug('tryReconnect: user is signed in.  got token');
          if (this.ws) {
            if (this.debug) console.debug('tryReconnect: ws exists.  unsubscribe');
            this.ws.unsubscribe();
          }
          this.ws = webSocket<object>(this.environment.ws);
          this.subscribe(t);
        });
      }
      else {
        if (this.debug) console.debug('try reconnect but user not signed in');
      }
    }, 10_000); // try in 10 seconds.
  }
}

